import { useState } from "react";
import { Control } from "react-hook-form";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useEmployeesList } from "@/hooks/useEmployeesList";

interface ReferralInfoFormProps {
  control: Control<any>;
  currentUserId?: string;
}

export const ReferralInfoForm = ({
  control,
  currentUserId,
}: ReferralInfoFormProps) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const { data: employees = [], isLoading } = useEmployeesList();
  const options = employees.filter((e) => e.id !== currentUserId);

  const filteredOptions =
    searchTerm.trim() === ""
      ? options
      : options.filter((emp) =>
          `${emp.firstName} ${emp.lastName} ${emp.email ?? ""}`
            .toLowerCase()
            .includes(searchTerm.toLowerCase()),
        );

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) setSearchTerm("");
  };

  return (
    <>
      <CardHeader>
        <CardTitle>Referral Info</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <FormField
          control={control}
          name="referredById"
          render={({ field }) => {
            const selected = options.find((e) => e.id === field.value) ?? null;

            return (
              <FormItem>
                <FormLabel>Referred By (optional)</FormLabel>
                <FormControl>
                  <div className="flex items-center gap-2">
                    <Popover open={open} onOpenChange={handleOpenChange}>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          role="combobox"
                          aria-expanded={open}
                          className="flex-1 justify-between font-normal"
                        >
                          {selected
                            ? `${selected.firstName} ${selected.lastName}`
                            : "Search employees..."}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>

                      <PopoverContent className="w-[340px] p-2" align="start">
                        <div className="space-y-2">
                          <input
                            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                            placeholder="Search by name or email..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            autoFocus
                          />

                          {isLoading ? (
                            <div className="py-4 text-center text-sm text-muted-foreground">
                              Loading...
                            </div>
                          ) : filteredOptions.length === 0 ? (
                            <div className="py-4 text-center text-sm text-muted-foreground">
                              No employees found.
                            </div>
                          ) : (
                            <div className="max-h-[260px] overflow-y-auto">
                              {filteredOptions.map((emp) => (
                                <div
                                  key={emp.id}
                                  className={cn(
                                    "flex items-center gap-2 rounded-md px-2 py-1.5 cursor-pointer hover:bg-accent hover:text-accent-foreground",
                                    field.value === emp.id &&
                                      "bg-accent text-accent-foreground",
                                  )}
                                  onClick={() => {
                                    field.onChange(emp.id);
                                    setOpen(false);
                                    setSearchTerm("");
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "h-4 w-4 shrink-0",
                                      field.value === emp.id
                                        ? "opacity-100"
                                        : "opacity-0",
                                    )}
                                  />
                                  <div className="flex flex-col min-w-0">
                                    <span className="font-medium truncate">
                                      {emp.firstName} {emp.lastName}
                                    </span>
                                    {emp.email && (
                                      <span className="text-xs text-muted-foreground truncate">
                                        {emp.email}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </PopoverContent>
                    </Popover>

                    {field.value && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => field.onChange(null)}
                        className="shrink-0 text-muted-foreground hover:text-foreground"
                        aria-label="Clear referral"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />
      </CardContent>
    </>
  );
};
